import type { ReactNode } from 'react';
import { createContext, createElement, useContext, useLayoutEffect } from 'react';

export type WorkspaceShellMode = 'default' | 'discovery-focus';

interface WorkspaceShellContextValue {
  mode: WorkspaceShellMode;
  setMode: (mode: WorkspaceShellMode) => void;
  isSidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  showSidebar: () => void;
}

const WorkspaceShellContext = createContext<WorkspaceShellContextValue | null>(null);

interface WorkspaceShellProviderProps {
  value: WorkspaceShellContextValue;
  children: ReactNode;
}

export function WorkspaceShellProvider({ value, children }: WorkspaceShellProviderProps) {
  return createElement(WorkspaceShellContext.Provider, { value }, children);
}

export function useWorkspaceShell() {
  const context = useContext(WorkspaceShellContext);
  if (!context) {
    throw new Error('useWorkspaceShell must be used within a WorkspaceShellProvider.');
  }

  return context;
}

export function useDiscoveryFocusShell(enabled: boolean) {
  const { mode, setMode, isSidebarOpen, setSidebarOpen, showSidebar } = useWorkspaceShell();

  useLayoutEffect(() => {
    if (!enabled) {
      setMode('default');
      return;
    }

    setMode('discovery-focus');

    return () => {
      setMode('default');
    };
  }, [enabled, setMode]);

  return {
    isFocusMode: enabled && mode === 'discovery-focus',
    isSidebarOpen,
    setSidebarOpen,
    showSidebar,
  };
}
